import React, { useEffect, useState } from 'react';
import { postService, Post } from '../../services/postService';

interface PostReport {
  id: number;
  post_id: number;
  reporter_id: number;
  reporter: {
    id: number;
    username: string;
    full_name?: string;
  };
  reason: string;
  status: string;
  created_at: string;
  reviewed_at?: string;
}

const AdminPostsPage: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [reports, setReports] = useState<PostReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [reportStatusFilter, setReportStatusFilter] = useState<string>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'posts' | 'reports'>('posts');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [postReportsCount, setPostReportsCount] = useState<Record<number, number>>({});
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectPostId, setRejectPostId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (activeTab === 'posts') {
      fetchPosts();
    } else {
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, reportStatusFilter, activeTab]);

  useEffect(() => {
    // Load reports count for posts
    if (activeTab === 'posts' && posts.length > 0) {
      fetchReportsCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, activeTab]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError('');
      let data;
      if (statusFilter === 'pending') {
        data = await postService.getPendingPosts({ page: 1, per_page: 100 });
      } else {
        data = await postService.getPosts({ page: 1, per_page: 100, status: statusFilter === 'all' ? undefined : statusFilter });
      }
      setPosts(data.posts || []);
    } catch (err: any) {
      console.error('Error fetching posts:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Không thể tải danh sách bài đăng';
      setError(errorMessage);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      setLoadingReports(true);
      setError('');
      const data = await postService.getReports({ 
        page: 1, 
        per_page: 100, 
        status: reportStatusFilter === 'all' ? undefined : reportStatusFilter 
      });
      setReports(data.reports || []);
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Không thể tải danh sách báo cáo';
      setError(errorMessage);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchReportsCount = async () => {
    try {
      const data = await postService.getReports({ page: 1, per_page: 1000, status: 'pending' });
      const counts: Record<number, number> = {};
      data.reports.forEach((report: PostReport) => {
        counts[report.post_id] = (counts[report.post_id] || 0) + 1;
      });
      setPostReportsCount(counts);
    } catch (err) {
      console.error('Error fetching reports count:', err);
    }
  };

  const handleReviewReport = async (reportId: number, action: 'reviewed' | 'resolved') => {
    try {
      await postService.reviewReport(reportId, action);
      setSuccess('Đã cập nhật trạng thái báo cáo');
      fetchReports();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Có lỗi xảy ra khi cập nhật báo cáo');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleApprove = async (postId: number) => {
    try {
      await postService.approvePost(postId);
      setSuccess('Bài đăng đã được duyệt');
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Có lỗi xảy ra khi duyệt bài đăng');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleReject = (postId: number) => {
    setRejectPostId(postId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectPostId) return;
    
    try {
      await postService.rejectPost(rejectPostId, rejectReason || undefined);
      setSuccess('Bài đăng đã bị từ chối');
      setShowRejectModal(false);
      setRejectPostId(null);
      setRejectReason('');
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Có lỗi xảy ra khi từ chối bài đăng');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleTogglePin = async (postId: number) => {
    try {
      await postService.togglePinPost(postId);
      setSuccess('Đã cập nhật trạng thái ghim');
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Có lỗi xảy ra');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (postId: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa bài đăng này?')) return;
    
    try {
      await postService.deletePost(postId);
      setSuccess('Bài đăng đã được xóa');
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Có lỗi xảy ra khi xóa bài đăng');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleViewDetail = async (postId: number) => {
    try {
      const post = await postService.getPost(postId);
      setSelectedPost(post);
      setShowDetailModal(true);
    } catch (err: any) {
      setError('Không thể tải chi tiết bài đăng');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    const labels = {
      pending: 'Chờ duyệt',
      approved: 'Đã duyệt',
      rejected: 'Đã từ chối',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    );
  };

  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      post.title.toLowerCase().includes(query) ||
      post.content.toLowerCase().includes(query) ||
      post.author.username.toLowerCase().includes(query) ||
      (post.author.full_name && post.author.full_name.toLowerCase().includes(query))
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Quản lý bài đăng</h1>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow rounded-xl border border-gray-200 p-1">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('posts')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === 'posts'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Bài đăng
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === 'reports'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Báo cáo ({reports.filter(r => r.status === 'pending').length})
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {activeTab === 'posts' ? (
        <>
          {/* Filters */}
          <div className="bg-white shadow rounded-xl border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Tìm kiếm theo tiêu đề, nội dung, tác giả..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    statusFilter === 'all'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Tất cả
                </button>
                <button
                  onClick={() => setStatusFilter('pending')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    statusFilter === 'pending'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Chờ duyệt ({posts.filter(p => p.status === 'pending').length})
                </button>
                <button
                  onClick={() => setStatusFilter('approved')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    statusFilter === 'approved'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Đã duyệt
                </button>
                <button
                  onClick={() => setStatusFilter('rejected')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    statusFilter === 'rejected'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Đã từ chối
                </button>
              </div>
            </div>
          </div>

          {/* Posts List */}
      <div className="bg-white shadow rounded-xl border border-gray-200">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Đang tải...</div>
        ) : filteredPosts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Không có bài đăng nào</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                className={`p-6 hover:bg-gray-50 transition ${
                  post.pinned ? 'bg-orange-50 border-l-4 border-l-orange-400' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{post.title}</h3>
                      {getStatusBadge(post.status)}
                      {post.pinned && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-medium">
                          📌 Đã ghim
                        </span>
                      )}
                      {postReportsCount[post.id] > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs font-medium">
                          ⚠️ {postReportsCount[post.id]} báo cáo
                        </span>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-3">
                      <span className="font-medium">Tác giả:</span> {post.author.full_name || post.author.username}
                      <span className="mx-2">•</span>
                      <span>{formatDate(post.created_at)}</span>
                      {post.approved_at && (
                        <>
                          <span className="mx-2">•</span>
                          <span>Duyệt: {formatDate(post.approved_at)}</span>
                        </>
                      )}
                    </div>

                    <p className="text-gray-700 mb-3 line-clamp-3 whitespace-pre-wrap">{post.content}</p>
                    
                    <button
                      onClick={() => handleViewDetail(post.id)}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium mb-3"
                    >
                      Xem chi tiết →
                    </button>

                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>👍 {post.likes_count}</span>
                      <span>💬 {post.comments_count}</span>
                    </div>

                    {post.rejected_reason && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">
                          <span className="font-medium">Lý do từ chối:</span> {post.rejected_reason}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {post.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(post.id)}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition whitespace-nowrap"
                        >
                          ✓ Duyệt
                        </button>
                        <button
                          onClick={() => handleReject(post.id)}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition whitespace-nowrap"
                        >
                          ✗ Từ chối
                        </button>
                      </>
                    )}
                    {post.status === 'approved' && (
                      <>
                        <button
                          onClick={() => handleTogglePin(post.id)}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition whitespace-nowrap"
                        >
                          {post.pinned ? '📌 Bỏ ghim' : '📌 Ghim'}
                        </button>
                        <button
                          onClick={() => handleReject(post.id)}
                          className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 transition whitespace-nowrap"
                        >
                          Từ chối
                        </button>
                      </>
                    )}
                    {post.status === 'rejected' && (
                      <button
                        onClick={() => handleApprove(post.id)}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition whitespace-nowrap"
                      >
                        Duyệt lại
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition whitespace-nowrap"
                    >
                      🗑️ Xóa
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      ) : (
        <>
          {/* Reports Filters */}
            <div className="bg-white shadow rounded-xl border border-gray-200 p-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setReportStatusFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    reportStatusFilter === 'all'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Tất cả
                </button>
                <button
                  onClick={() => setReportStatusFilter('pending')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    reportStatusFilter === 'pending'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Chờ xử lý ({reports.filter(r => r.status === 'pending').length})
                </button>
                <button
                  onClick={() => setReportStatusFilter('reviewed')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    reportStatusFilter === 'reviewed'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Đã xem xét
                </button>
                <button
                  onClick={() => setReportStatusFilter('resolved')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    reportStatusFilter === 'resolved'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Đã giải quyết
                </button>
              </div>
            </div>

            {/* Reports List */}
            <div className="bg-white shadow rounded-xl border border-gray-200">
              {loadingReports ? (
                <div className="p-8 text-center text-gray-500">Đang tải...</div>
              ) : reports.length === 0 ? (
                <div className="p-8 text-center text-gray-500">Không có báo cáo nào</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {reports.map((report) => (
                    <div key={report.id} className="p-6 hover:bg-gray-50 transition">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-sm font-medium text-gray-500">Báo cáo #{report.id}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              report.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              report.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {report.status === 'pending' ? 'Chờ xử lý' :
                               report.status === 'reviewed' ? 'Đã xem xét' : 'Đã giải quyết'}
                            </span>
                          </div>
                          
                          <div className="text-sm text-gray-600 mb-3">
                            <span className="font-medium">Người báo cáo:</span> {report.reporter?.full_name || report.reporter?.username}
                            <span className="mx-2">•</span>
                            <span>Bài viết ID: {report.post_id}</span>
                            <span className="mx-2">•</span>
                            <span>{formatDate(report.created_at)}</span>
                          </div>

                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
                            <p className="text-gray-900 whitespace-pre-wrap">{report.reason}</p>
                          </div>

                          <button
                            onClick={async () => {
                              const post = await postService.getPost(report.post_id);
                              setSelectedPost(post);
                              setShowDetailModal(true);
                            }}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            Xem bài viết được báo cáo →
                          </button>
                        </div>

                        <div className="flex flex-col gap-2">
                          {report.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleReviewReport(report.id, 'reviewed')}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition whitespace-nowrap"
                              >
                                Đánh dấu đã xem
                              </button>
                              <button
                                onClick={() => handleReviewReport(report.id, 'resolved')}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition whitespace-nowrap"
                              >
                                Đánh dấu đã giải quyết
                              </button>
                            </>
                          )}
                          {report.status === 'reviewed' && (
                            <button
                              onClick={() => handleReviewReport(report.id, 'resolved')}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition whitespace-nowrap"
                            >
                              Đánh dấu đã giải quyết
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

      {/* Detail Modal */}
      {showDetailModal && selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Chi tiết bài đăng</h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedPost(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-2xl font-bold text-gray-900">{selectedPost.title}</h3>
                    {getStatusBadge(selectedPost.status)}
                    {selectedPost.pinned && (
                      <span className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-medium">
                        📌 Đã ghim
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                    <div>
                      <span className="font-medium">Tác giả:</span>{' '}
                      {selectedPost.author.full_name || selectedPost.author.username}
                      <span className="text-gray-400 ml-1">(@{selectedPost.author.username})</span>
                    </div>
                    <div>
                      <span className="font-medium">Vai trò:</span>{' '}
                      <span className="capitalize">{selectedPost.author.role}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Ngày tạo:</span>
                      <div className="font-medium text-gray-900">{formatDate(selectedPost.created_at)}</div>
                    </div>
                    {selectedPost.updated_at && (
                      <div>
                        <span className="text-gray-500">Cập nhật:</span>
                        <div className="font-medium text-gray-900">{formatDate(selectedPost.updated_at)}</div>
                      </div>
                    )}
                    {selectedPost.approved_at && (
                      <div>
                        <span className="text-gray-500">Duyệt lúc:</span>
                        <div className="font-medium text-gray-900">{formatDate(selectedPost.approved_at)}</div>
                      </div>
                    )}
                    {selectedPost.approved_by && (
                      <div>
                        <span className="text-gray-500">Người duyệt:</span>
                        <div className="font-medium text-gray-900">
                          {selectedPost.approved_by.full_name || selectedPost.approved_by.username}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags */}
              {selectedPost.tags && selectedPost.tags.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-700 mb-2 block">Tags:</span>
                  <div className="flex flex-wrap gap-2">
                    {selectedPost.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Content */}
              <div>
                <span className="text-sm font-medium text-gray-700 mb-2 block">Nội dung:</span>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{selectedPost.content}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div>
                  <span className="font-medium">👍 Likes:</span> {selectedPost.likes_count}
                </div>
                <div>
                  <span className="font-medium">💬 Comments:</span> {selectedPost.comments_count}
                </div>
              </div>

              {/* Rejected Reason */}
              {selectedPost.rejected_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <span className="text-sm font-medium text-red-700 block mb-2">Lý do từ chối:</span>
                  <p className="text-red-800">{selectedPost.rejected_reason}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                {selectedPost.status === 'pending' && (
                  <>
                    <button
                      onClick={async () => {
                        await handleApprove(selectedPost.id);
                        setShowDetailModal(false);
                        setSelectedPost(null);
                      }}
                      className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition"
                    >
                      ✓ Duyệt bài đăng
                    </button>
                    <button
                      onClick={async () => {
                        await handleReject(selectedPost.id);
                        setShowDetailModal(false);
                        setSelectedPost(null);
                      }}
                      className="px-6 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition"
                    >
                      ✗ Từ chối bài đăng
                    </button>
                  </>
                )}
                {selectedPost.status === 'approved' && (
                  <>
                    <button
                      onClick={async () => {
                        await handleTogglePin(selectedPost.id);
                        const updatedPost = await postService.getPost(selectedPost.id);
                        setSelectedPost(updatedPost);
                      }}
                      className="px-6 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition"
                    >
                      {selectedPost.pinned ? '📌 Bỏ ghim' : '📌 Ghim bài đăng'}
                    </button>
                    <button
                      onClick={async () => {
                        await handleReject(selectedPost.id);
                        setShowDetailModal(false);
                        setSelectedPost(null);
                      }}
                      className="px-6 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition"
                    >
                      Từ chối bài đăng
                    </button>
                  </>
                )}
                {selectedPost.status === 'rejected' && (
                  <button
                    onClick={async () => {
                      await handleApprove(selectedPost.id);
                      setShowDetailModal(false);
                      setSelectedPost(null);
                    }}
                    className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition"
                  >
                    Duyệt lại bài đăng
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (window.confirm('Bạn có chắc chắn muốn xóa bài đăng này?')) {
                      await handleDelete(selectedPost.id);
                      setShowDetailModal(false);
                      setSelectedPost(null);
                    }
                  }}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition"
                >
                  🗑️ Xóa bài đăng
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedPost(null);
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition ml-auto"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowRejectModal(false);
            setRejectPostId(null);
            setRejectReason('');
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">Từ chối bài đăng</h3>
            <p className="text-gray-600 mb-4">Nhập lý do từ chối (tùy chọn):</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập lý do từ chối..."
              autoFocus
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectPostId(null);
                  setRejectReason('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                Hủy
              </button>
              <button
                onClick={handleConfirmReject}
                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
              >
                Từ chối
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPostsPage;

